import type { CallSummary, MediaErrorKind } from "@backstory/protocol";
import { mediaErrorLabel, qualityBucket } from "@backstory/player";

/**
 * Shared call presentation. Quality is a status encoding, so every use carries
 * an icon and a label and never relies on color alone.
 */

const BUCKET_ICON: Record<string, string> = { good: "●", fair: "◐", poor: "◔", bad: "○", unknown: "–" };

export function QualityMeter({ score, width = 80, showLabel = true }: { score?: number; width?: number; showLabel?: boolean }) {
  const b = qualityBucket(score);
  const pct = Math.max(0, Math.min(100, score ?? 0));
  return (
    <span className="call-q" title="Composite of latency, loss, jitter, freezes, and audio concealment (100 is best)">
      <span className="call-q-track" style={{ width }} role="img" aria-label={score == null ? "Quality score unavailable" : `Quality score ${score} of 100, ${b.label}`}>
        <i style={{ width: `${Math.max(2, pct)}%`, background: `var(${b.colorVar})` }} />
      </span>
      {showLabel && (
        <span className="call-q-label">
          <span aria-hidden="true">{BUCKET_ICON[b.id]}</span> {score == null ? "—" : score} <span className="muted">{b.label}</span>
        </span>
      )}
    </span>
  );
}

const GROUP_STATUS: Record<string, { cls: string; label: string }> = {
  healthy: { cls: "ok", label: "Healthy" },
  degraded: { cls: "warn", label: "Degraded" },
  critical: { cls: "danger", label: "Critical" },
  ended: { cls: "neutral", label: "Ended" },
  unknown: { cls: "neutral", label: "No score yet" },
};

export function GroupStatusBadge({ status }: { status: string }) {
  const s = GROUP_STATUS[status] ?? GROUP_STATUS.unknown!;
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
}

export function TopologyBadge({ topology }: { topology?: string }) {
  const label = topology === "sfu" ? "Media server" : topology === "peer" ? "Direct" : topology === "mixed" ? "Mixed paths" : "";
  if (!label) return null;
  return <span className="badge neutral">{label}</span>;
}

export function PathBadge({ relayed }: { relayed: boolean }) {
  return (
    <span className={`badge ${relayed ? "warn" : "neutral"}`} title={relayed ? "Peer-to-peer was blocked, so media went through a TURN relay: expect higher latency" : "Direct peer-to-peer media path"}>
      <span aria-hidden="true">{relayed ? "⇄" : "→"}</span> {relayed ? "relayed" : "direct"}
    </span>
  );
}

export function CallStateBadge({ call }: { call: Pick<CallSummary, "failedToConnect" | "endedAt"> }) {
  if (call.failedToConnect) {
    return (
      <span className="badge danger" title="The call never reached the connected state">
        <span aria-hidden="true">!</span> never connected
      </span>
    );
  }
  if (!call.endedAt) {
    return (
      <span className="badge warn" title="The call is still open">
        in progress
      </span>
    );
  }
  return <span className="badge ok">connected</span>;
}

export function MediaErrorBadges({ errors }: { errors: MediaErrorKind[] }) {
  if (!errors?.length) return null;
  return (
    <>
      {errors.map((e) => (
        <span key={e} className="badge danger" title={`Media capture failed: ${mediaErrorLabel(e)}`}>
          {mediaErrorLabel(e)}
        </span>
      ))}
    </>
  );
}

export function dur(ms: number | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

export function pctText(n: number | undefined, digits = 1): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

/** Fraction 0..1 rendered as a percentage; the API returns rates as fractions. */
export function rateText(n: number | undefined, digits = 1): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}